/**
 * Callback type for upload progress updates
 */
export type UploadProgressCallback = (progress: number) => void

/**
 * Extract image metadata (dimensions, aspect ratio) from a File object
 * Returns null for non-image files or if metadata extraction fails
 */
export async function getImageMetadata(file: File): Promise<{
  width: number
  height: number
  aspectRatio: number
  format: string
} | null> {
  if (!file.type.startsWith('image/')) {
    return null
  }

  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      const metadata = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
        format: file.type.split('/')[1] || 'unknown',
      }
      URL.revokeObjectURL(objectUrl)
      resolve(metadata)
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(null)
    }

    img.src = objectUrl
  })
}

/**
 * Generic file upload utility that uploads directly to BunnyCDN
 * Supports optional progress tracking via callback
 */
export async function uploadFileToService(
  file: File,
  onProgress?: UploadProgressCallback
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    // 500 MB size limit
    const maxSize = 500 * 1024 * 1024
    if (file.size > maxSize) {
      return { success: false, error: 'File is too large (max 500 MB)' }
    }

    // Step 1: Get upload URL and credentials from backend
    const credentialsResponse = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type,
        }),
      }
    )

    if (!credentialsResponse.ok) {
      try {
        const errorData = await credentialsResponse.json()
        return {
          success: false,
          error:
            errorData.error ||
            `Failed to get upload credentials: ${credentialsResponse.status} ${credentialsResponse.statusText}`,
        }
      } catch {
        return {
          success: false,
          error: `Failed to get upload credentials: ${credentialsResponse.status} ${credentialsResponse.statusText}`,
        }
      }
    }

    const { uploadUrl, cdnUrl, storageKey } = await credentialsResponse.json()

    if (!uploadUrl || !cdnUrl || !storageKey) {
      return { success: false, error: 'Invalid upload credentials received' }
    }

    // Step 2: Upload file directly to BunnyCDN
    if (onProgress) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest()

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100)
            onProgress(percentComplete)
          }
        })

        // Handle upload completion
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true, url: cdnUrl })
          } else {
            let errorMessage = `File upload failed: ${xhr.status} ${xhr.statusText}`
            try {
              const errorBody = JSON.parse(xhr.responseText)
              if (errorBody.Message) {
                errorMessage = errorBody.Message
              }
            } catch {
              // If can't parse JSON, use default error
            }
            resolve({ success: false, error: errorMessage })
          }
        })

        xhr.addEventListener('error', () => {
          resolve({ success: false, error: 'Network error during file upload' })
        })

        xhr.addEventListener('abort', () => {
          resolve({ success: false, error: 'File upload cancelled' })
        })

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('AccessKey', storageKey)
        xhr.setRequestHeader('Content-Type', 'application/octet-stream')
        xhr.send(file)
      })
    } else {
      // Use standard fetch for simpler uploads without progress
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          AccessKey: storageKey,
          'Content-Type': 'application/octet-stream',
        },
      })

      if (!uploadResponse.ok) {
        try {
          const errorData = await uploadResponse.json()
          return {
            success: false,
            error:
              errorData.Message ||
              `File upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          }
        } catch {
          return {
            success: false,
            error: `File upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          }
        }
      }

      return { success: true, url: cdnUrl }
    }
  } catch (error) {
    console.error('File upload failed:', error)
    return { success: false, error: 'File upload failed' }
  }
}
